import React, {
  useState, useEffect, useContext, useCallback,
} from 'react';
import _ from 'lodash';
import {useRouter} from 'next/router';

import {AcademicCapIcon as AcademicCapIconHero} from '@heroicons/react-v2/20/solid';
import {SearchIcon} from '../../components/svg';
import {CourseCarousel} from '../../components/LinkedData';
import {FadeOut} from '../../components/FadeOut';
import CourseCard, {Placeholder} from './CourseCard';
import {CourseLessonsContext} from '../../components/CourseLessonsProvider';
import {CourseVisits} from '../../../../lib/constants/courses';
import {useAnalytics, useAppContext} from '../../hooks';
import {cx, createURLWithParams} from '../../util';
import {getRecommendations} from '../../api/api';
import {Searcher} from '../../../../lib/fuzzySearch';

const DEFAULT_NUM_RESULTS = 10;

// tag should correspond to tag.id in the database
const ChicletOptions = [
  {chiclet: 'All', tag: null},
  {chiclet: 'PM', tag: 'pm'},
  {chiclet: 'EM', tag: 'em'},
  {chiclet: 'SWE', tag: 'swe'},
  {chiclet: 'FDE', tag: 'forward-deployed-engineer'},
  {chiclet: 'Investment Banking', tag: 'investment-banking'},
  {chiclet: 'Data Engineering', tag: 'data-engineer'},
  {chiclet: 'Machine Learning', tag: 'ml-engineer'},
  {chiclet: 'Cybersecurity', tag: 'security'},
  {chiclet: 'Behavioral', tag: 'behavioral'},
  {chiclet: 'System Design', tag: 'system-design'},
  {chiclet: 'TPM', tag: 'tpm'},
  {chiclet: 'UX / Product Design', tag: 'product-designer'},
  {chiclet: 'Product Analytics', tag: 'product-analyst'},
  {chiclet: 'Data Science', tag: 'data-science'},
  {chiclet: 'Data Analytics', tag: 'data-analyst'},
  {chiclet: 'SQL', tag: 'sql'},
  {chiclet: 'Amazon', tag: 'amazon'},
  {chiclet: 'Google', tag: 'google'},
  {chiclet: 'Meta', tag: 'meta-facebook'},
  {chiclet: 'Solutions Architect', tag: 'solutions-architect'},
  {chiclet: 'BizOps', tag: 'bizops'},
];

const SearchBar = ({value, onChange}) => (
  <div className="relative">
    <SearchIcon className="absolute left-4 h-4 w-4 pointer-events-none text-gray-400" style={{top: '50%', transform: 'translateY(-50%)'}} />
    <input
      placeholder="Search courses or topics..."
      className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-20 focus:border-indigo-400 text-sm transition-all"
      value={value}
      onChange={onChange}
      type="search"
      aria-label="Search courses"
    />
  </div>
);

const Chiclet = ({
  name, tag: chicletTag, selected, onClick = _.noop, updateParams,
}) => (
  <button
    type="button"
    key={name}
    onClick={() => {
      onClick(name);
      updateParams({tag: chicletTag, query: null});
    }}
    className={cx('px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all border flex-shrink-0', {
      'bg-gray-900 text-white border-gray-900': selected,
      'bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:text-gray-900': !selected,
    })}
  >
    {name}
  </button>
);

const sortSearchResults = (coursesAndTracks, recommendations) => {
  const [recommended, rest] = _.partition(
    coursesAndTracks,
    ({item}) => recommendations?.includes(item?.slug),
  );

  const [tracks, courses] = _.partition(
    rest,
    ({item}) => item?.contentType === 'track',
  );

  return [...recommended, ...tracks, ...courses];
};

const CourseLibrary = ({
  tag, query, updateParams, tagMap,
}) => {
  const {currentUser} = useAppContext();
  const {courses, loading: providerLoading} = useContext(CourseLessonsContext);
  const [searchQuery, setSearchQuery] = useState(query);
  const [fuzzySearcher, setFuzzySearcher] = useState(null);
  const [filteredResults, setFilteredResults] = useState([]);
  const [recommendations, setRecommendations] = useState();
  const [loading, setLoading] = useState(true);
  const {tracker} = useAnalytics();
  const router = useRouter();

  // Set up fuse search
  useEffect(() => {
    if (!providerLoading) {
      const availableCourses = courses.filter((c) => c.availableInCatalog);
      const searcher = new Searcher(availableCourses, ['name', 'title', 'description', 'tags'], {
        threshold: 0.3,
      });
      setFuzzySearcher(searcher);
    }
  }, [providerLoading]);

  // Load recommended courses
  useEffect(() => {
    if (currentUser) {
      getRecommendations().then((recs) => {
        setRecommendations(recs.courses.map((rec) => rec.slug));
      });
    }
  }, [currentUser]);

  // Run a search for query or tag, or clear filtering
  useEffect(() => {
    if (fuzzySearcher) {
      const searchResults = fuzzySearcher.search(query || tagMap[tag] || tag);
      setFilteredResults(sortSearchResults(searchResults, recommendations));
      setLoading(false);
    }
  }, [fuzzySearcher, query, tag, tagMap, recommendations]);

  const completeSearch = useCallback(_.debounce(
    (value) => {
      updateParams({query: value, tag: null});
      tracker.courseListSearch({value});
    },
    500,
  ), [updateParams]);

  const handleSearchQueryChange = ({target: {value}}) => {
    setSearchQuery(value);
    completeSearch(value);
  };

  const handleChicletChange = (value) => {
    tracker.courseListClickChiclet({value});
    setSearchQuery('');
  };

  const clearQuery = () => {
    updateParams({query: null, tag: null});
    setSearchQuery('');
  };

  const isLoading = loading || providerLoading;

  const onClickCourse = (item) => (e) => {
    e.preventDefault();
    tracker.courseListClickCourse({
      courseID: item.slug,
      recommended: recommendations?.includes(item.slug),
    });
    router.push(`/courses/${item.slug}`);
  };

  return (
    <div>
      <CourseCarousel courses={_.map(filteredResults, 'item')} />
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6" style={{gap: '24px'}}>
        <div className="max-w-2xl">
          <h1 className="text-4xl font-bold text-gray-900 tracking-tight mb-3">
            {tag in tagMap ? `${tagMap[tag]} Courses` : 'Course Library'}
          </h1>
          <p className="text-lg text-gray-500 leading-relaxed">
            Structured interview prep paths designed by experts and tailored for specific
            roles, companies, and skills.
          </p>
        </div>
        <div className="w-full max-w-xs lg:max-w-sm">
          <SearchBar value={searchQuery} onChange={handleSearchQueryChange} />
        </div>
      </div>

      {/* Filter Chiclets */}
      <div className="mb-6">
        <FadeOut right left>
          <div className="flex items-center pb-2" style={{gap: '8px'}}>
            {ChicletOptions.map(({chiclet, tag: chicletTag}) => (
              <Chiclet
                name={chiclet}
                tag={chicletTag}
                selected={chicletTag === tag || (!tag && chiclet === 'All')}
                onClick={handleChicletChange}
                updateParams={updateParams}
                key={chiclet}
              />
            ))}
          </div>
        </FadeOut>
      </div>
      <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {isLoading && _.times(DEFAULT_NUM_RESULTS, () => <Placeholder />)}
        {filteredResults.map(({item}) => (
          <CourseCard
            key={item.slug}
            name={item.shortTitle}
            href={`/courses/${item.slug}`}
            description={item.description}
            thumbnail={item.thumbnail}
            numLessons={item.lessons?.length}
            numCourses={item.courses?.length}
            numStudents={CourseVisits[item.slug]}
            onClick={onClickCourse(item)}
            badge={recommendations?.includes(item.slug) ? 'Recommended' : null}
            isPath={item.contentType === 'track'}
            tags={item.tags}
          />
        ))}
      </div>
      {!isLoading && (
        <p className="sr-only" role="status" aria-live="polite">
          {filteredResults.length
            ? `${filteredResults.length} results returned`
            : 'No results returned'}
        </p>
      )}
      {!isLoading && !filteredResults.length && (
        <div className="mt-8 flex flex-col items-center">
          <span className="text-lg font-semibold text-gray-600">
            Sorry, there aren&rsquo;t any courses that match your search.
          </span>
          <button
            type="button"
            onClick={clearQuery}
            className="mt-4 px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Clear filters
          </button>
        </div>
      )}
    </div>
  );
};

export default CourseLibrary;
